import { Button } from '@mui/material';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useFormErrorNotificationContext } from '../../../../contexts/form-error-notification-context/form-error-notification-context';
import { queryKeys } from '../../../../services/query-key/query-keys';
import { runMutation } from '../../../../utils/vy-mutation';
import type { CreateCreditNoteFormValues } from './CreateCreditNoteForm/CreateCreditNoteForm';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { userMessage } from '../../../../utils/vy-toasts';
import { isNullOrUndefined } from '@vy/utils';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';
import { useCreateCreditNote } from '../../../../hooks/requests/use-create-credit-note';

export const CreateCreditNoteBottomToolbar: React.FC = () => {
  const { trigger, reset, getValues } = useFormContext<CreateCreditNoteFormValues>();
  const { setShowErrorNotification } = useFormErrorNotificationContext();
  const isNetSuiteConnected = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);
  const isQuickBooksConnected = useActivatedIntegration(INTEGRATION_PROVIDERS.quickBooks);
  const isXeroConnected = useActivatedIntegration(INTEGRATION_PROVIDERS.xero);
  const createCreditNoteMutation = useCreateCreditNote();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSubmit = useCallback(async () => {
    const formValues = getValues();
    const validForm = await trigger();

    if (!validForm) {
      setShowErrorNotification(true);
      return;
    }

    const erpId =
      formValues.netSuiteItemId ?? formValues.quickBooksItemId ?? formValues.xeroItemCode;

    const anyErpConnected = isNetSuiteConnected || isQuickBooksConnected || isXeroConnected;
    if (anyErpConnected && isNullOrUndefined(erpId)) {
      userMessage('error', 'Product selection is required when an ERP is connected');
      return;
    }

    const customerId = formValues.customerId;

    await runMutation(
      createCreditNoteMutation,
      { ...formValues, erpId },
      {
        pending: 'Saving...',
        success: 'Saved',
        error: 'Failed to save credit note',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.creditLedgerOverview(customerId)),
            queryClient.invalidateQueries(queryKeys.creditNotes(customerId)),
          ]);
          navigate(`/customers/${customerId}/overview`);
        },
      },
    );
  }, [
    getValues,
    trigger,
    createCreditNoteMutation,
    setShowErrorNotification,
    queryClient,
    navigate,
    isNetSuiteConnected,
    isQuickBooksConnected,
    isXeroConnected,
  ]);

  const handleClearForm = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <div className="flex justify-end gap-x-2 border-t border-t-[#DFEBF1] bg-white p-4">
      <Button
        variant="text"
        color="primary"
        size="large"
        disableElevation
        className="rounded-lg text-sm font-medium disabled:bg-[#EAEEFC] disabled:text-[#A3C8FF]"
        onClick={handleClearForm}
      >
        Clear form
      </Button>

      <Button
        variant="contained"
        color="primary"
        size="medium"
        disableElevation
        className="rounded-lg text-sm font-medium disabled:bg-[#EAEEFC] disabled:text-[#A3C8FF]"
        onClick={handleSubmit}
        type="submit"
      >
        Create Credit Note
      </Button>
    </div>
  );
};
